import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";

function fmtDate(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  return d.toLocaleDateString();
}

export default function PlanRenewal() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [requests, setRequests] = useState([]);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const dateRef = useRef(null);

  const [form, setForm] = useState({
    plan_id: "",
    payment_preference: "UPI",
    payment_reference: "",
    payment_reference_date: "",
  });

  const phoneMissing = useMemo(() => {
    const p = String(profile.phone || "").replace(/\D/g, "");
    return !p;
  }, [profile.phone]);

  const selectedPlan = useMemo(() => {
    const id = Number(form.plan_id);
    if (!Number.isFinite(id)) return null;
    return plans.find((p) => Number(p.id) === id) || null;
  }, [form.plan_id, plans]);

  const durationLabel = useMemo(() => {
    const d = Number(selectedPlan?.duration_days);
    if (!Number.isFinite(d) || d <= 0) return "—";
    return `${d} days`;
  }, [selectedPlan]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [plansRes, reqRes, profRes] = await Promise.all([
        axios.get("/client/plans"),
        axios.get("/client/plan-renewal-requests"),
        axios.get("/client/profile"),
      ]);

      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      setRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
      setProfile({
        name: profRes.data?.name || "",
        email: profRes.data?.email || "",
        phone: profRes.data?.phone || "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load plan renewal data";
      alert(msg);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!form.plan_id) {
      alert("Please select a plan");
      return;
    }

    const pref = String(form.payment_preference).toUpperCase();
    if (pref === "UPI" || pref === "BANK") {
      if (!form.payment_reference || !form.payment_reference_date) {
        alert("Payment requires reference and date");
        return;
      }
    }

    try {
      await axios.post("/client/plan-renewal-requests", {
        ...form,
      });
      alert("Renewal request submitted");
      setForm({
        plan_id: "",
        payment_preference: "UPI",
        payment_reference: "",
        payment_reference_date: "",
      });
      await loadAll();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit request";
      alert(msg);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this request?")) return;
    try {
      await axios.post(`/client/plan-renewal-requests/${id}/cancel`);
      await loadAll();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to cancel";
      alert(msg);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarClient />

      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Plan Renewal</h1>
            <p className="text-gray-500 mt-1">Request plan renewal and track approval status.</p>
          </div>
          <button
            onClick={loadAll}
            className="bg-gray-100 text-gray-700 px-5 py-2 rounded-xl font-bold hover:bg-gray-200 transition"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {phoneMissing && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-2xl">
                <p className="font-bold">Email missing</p>
                <p className="text-sm mt-1">
                  Admin will send your invoice PDF to your registered email.
                  Please add your email in <b>Settings → Profile</b>.
                </p>
              </div>
            )}

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">New Renewal Request</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">SELECT PLAN</label>
                  <select
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={form.plan_id}
                    onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                  >
                    <option value="">-- Select --</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (₹{p.price})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">DURATION</label>
                  <input
                    disabled
                    className="w-full border p-3 rounded-xl mt-1 bg-gray-50 text-gray-700"
                    value={durationLabel}
                    readOnly
                  />
                  <p className="text-xs text-gray-400 mt-1 ml-1">Duration is fixed by the selected plan.</p>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">PAYMENT PREFERENCE</label>
                  <select
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={form.payment_preference}
                    onChange={(e) => setForm({ ...form, payment_preference: e.target.value })}
                  >
                    <option value="UPI">UPI</option>
                    <option value="BANK">BANK</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">PAYMENT REF (UPI/BANK)</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Transaction/UPI reference"
                    value={form.payment_reference}
                    onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                  />
                  <p className="text-xs text-gray-400 mt-1 ml-1">Required for UPI/BANK</p>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">PAYMENT REF DATE (UPI/BANK)</label>
                  <div
                    className="w-full border p-3 rounded-xl mt-1 focus-within:ring-2 focus-within:ring-blue-500 outline-none cursor-pointer"
                    onClick={() => {
                      dateRef.current?.showPicker?.();
                      dateRef.current?.focus();
                    }}
                  >
                    <input
                      ref={dateRef}
                      type="date"
                      className="w-full bg-transparent outline-none"
                      value={form.payment_reference_date}
                      onChange={(e) => setForm({ ...form, payment_reference_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={submit}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition"
                  >
                    Submit Request
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">My Requests</h2>

              {requests.length === 0 ? (
                <p className="text-gray-500">No requests yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2">Date</th>
                        <th>Plan</th>
                          <th>Amount</th>
                          <th>Duration</th>
                          <th>Pref</th>
                          <th>Ref</th>
                          <th>Ref Date</th>
                          <th>Status</th>
                          <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((r) => (
                        <tr key={r.id} className="border-b last:border-b-0">
                          <td className="py-2">{fmtDate(r.created_at)}</td>
                          <td>{r.plan_name}</td>
                          <td>₹{r.plan_price}</td>
                          <td>{Number(r.duration_days) ? `${r.duration_days} days` : "—"}</td>
                          <td>{String(r.payment_preference || "").toUpperCase()}</td>
                          <td>{r.payment_reference || "-"}</td>
                          <td>{r.payment_reference_date ? fmtDate(r.payment_reference_date) : "-"}</td>
                          <td>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-bold ${
                                String(r.status).toUpperCase() === "APPROVED"
                                  ? "bg-green-100 text-green-700"
                                  : String(r.status).toUpperCase() === "PENDING"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : String(r.status).toUpperCase() === "REJECTED"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {String(r.status).toUpperCase()}
                            </span>
                          </td>
                          <td className="text-right">
                            {String(r.status).toUpperCase() === "PENDING" && (
                              <button
                                onClick={() => cancel(r.id)}
                                className="text-red-600 font-bold hover:underline"
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
